import type { Membership, User } from '@hostesa/api-client';

/**
 * Bridges React-held auth state into the (non-React) TanStack Router context so
 * `beforeLoad` guards can read the current session synchronously without a
 * hook. The `AuthProvider` lives in React; this holder is updated from a thin
 * `AuthBridge` component on every auth-state change, and the router context
 * reads the latest snapshot when evaluating guards.
 *
 * Guards are intentionally synchronous and side-effect-free — they read this
 * snapshot and return a redirect/throw decision. The backend remains the
 * security boundary; this is the UX/early-rejection layer.
 */
export interface AuthSnapshot {
  token: string | null;
  user: User | null;
  memberships: Membership[];
  /** True while the initial `GET /me` (session restore) is in flight. */
  loading: boolean;
}

const EMPTY_SNAPSHOT: AuthSnapshot = {
  token: null,
  user: null,
  memberships: [],
  loading: false,
};

let current: AuthSnapshot = EMPTY_SNAPSHOT;

export function setAuthSnapshot(next: AuthSnapshot): void {
  current = next;
}

export function getAuthSnapshot(): AuthSnapshot {
  return current;
}
